import React from 'react';
import { FormValue, useField } from 'informed';

import { useBranches } from '../../hooks/useBranches';
import {
  getBranchDetailsFromBranchId,
  getBranchIdFromBranchName
} from '../../utils/ComboBoxUtils';

import { BranchesComboBox } from '../BranchesComboBox';
import { BranchComboBoxProps } from '../BranchesComboBox/BranchComboBox';

import { FieldContainer } from './styledComponents';

export interface InformedBranchComboboxProps extends BranchComboBoxProps {
  isHidden?: boolean;
  field: string;
  supportingField: string;
  validationSchemaObject: any;
  touched: boolean;
  isDisabled?: boolean;
  reRenderObjects?: Array<any>;
  fieldWrapperStyles?: React.CSSProperties;
  initialBranchValue?: string;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  enableDataMask?: boolean;
}

export const InformedBranchCombobox = (props: InformedBranchComboboxProps) => {
  const {
    isHidden,
    field,
    supportingField,
    validationSchemaObject,
    fieldWrapperStyles,
    touched,
    onSelectionChange,
    isDisabled,
    label,
    error,
    onBlur: onBlurPropFunc,
    reRenderObjects,
    initialBranchValue,
    validateOnBlur = true,
    validateOnChange = true,
    enableDataMask,
    ...comboboxProps
  } = props;

  const { fieldState, render, informed } = useField<FormValue<string>>({
    field,
    validateOnBlur: false,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { fieldApi } = useField<FormValue<string>>({ field: supportingField });

  const { value, error: fieldError } = fieldState;
  const { onChange, onBlur } = informed;

  const onBlurFunction = (e) => {
    onBlur(e.target.value);
    onBlurPropFunc && onBlurPropFunc(e.target.value);
  };

  const { branches } = useBranches();

  const onSelectionChangeFunc = (value) => {
    const branch = getBranchDetailsFromBranchId(value, branches.current);

    if (branch) {
      if (value === 'Other') {
        const { setValue } = fieldApi;
        setValue(branch.branch_name);
        onChange('Other');
      } else {
        onChange(branch.branch_name);
      }

      onSelectionChange && onSelectionChange(branch?.branch_id);
    } else {
      const { setValue } = fieldApi;
      setValue('');
      onChange('');
      onSelectionChange && onSelectionChange('');
    }
  };

  const getSelectedKey = (): string => {
    if (value === 'Other') {
      return value;
    } else {
      return getBranchIdFromBranchName(value, branches.current);
    }
  };

  const renderBranchCombobox = () => {
    return (
      <BranchesComboBox
        label={label}
        selectedKey={getSelectedKey()}
        onBlur={onBlurFunction}
        isDisabled={isDisabled}
        onSelectionChange={onSelectionChangeFunc}
        error={fieldError}
        initialBranchValue={initialBranchValue}
        enableDataMask={enableDataMask}
        {...comboboxProps}
      />
    );
  };

  return render(
    <FieldContainer isHidden={isHidden} css={fieldWrapperStyles}>
      {renderBranchCombobox()}
    </FieldContainer>
  );
};
